import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, X, Zap, LocateFixed, AlertTriangle, RefreshCw, Loader2, Sparkles, ChevronLeft, Trash2 } from 'lucide-react';
import { fields as fieldsApi, ApiError } from '../lib/api.js';
import { useSession } from '../lib/session.jsx';
import { useGeolocation } from '../lib/useGeolocation.js';
import { distanceMeters } from '../lib/geo.js';
import { CHECKIN_GRACE_M, reenergizeCost } from '../lib/config.js';
import { syncGeofences, onGeofenceTransition } from '../lib/fieldAlerts.js';
import EnergyMap from '../components/EnergyMap.jsx';

const OUT_OF_FIELD_MS = 30_000;
import FieldModal from '../components/FieldModal.jsx';
import { Button, Tag } from '../components/ui.jsx';
import { TokenPill } from '../components/Layout.jsx';

export default function HealingMap() {
  const nav = useNavigate();
  const { device, tokens, refresh, isAdmin } = useSession();
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [params] = useSearchParams();
  const { position, status, retry } = useGeolocation({ watch: true });
  const [list, setList] = useState([]);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(null);
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);
  const [focus, setFocus] = useState(null);

  const load = useCallback(async (at) => {
    if (!at) return;
    try { setList(await fieldsApi.nearby({ lat: at.lat, lng: at.lng, withinMeters: 5000 })); }
    catch (e) { console.error(e); }
  }, []);

  useEffect(() => { load(position); }, [position?.lat, position?.lng, load]);

  // Field alerts: keep the phone's geofences equal to what is on the map, and
  // refresh when the OS reports a boundary crossing while the app is open.
  useEffect(() => { if (device?.field_alerts) syncGeofences(list, true); }, [list, device?.field_alerts]);
  useEffect(() => onGeofenceTransition(() => { load(position); refresh(); }), [position, load, refresh]);

  // Opened from a push: select that field so the Connect choice is right there.
  useEffect(() => {
    const id = params.get('field');
    if (!id || !list.length) return;
    const f = list.find((x) => x.id === id);
    if (f) { setSelected(id); setFocus({ lat: f.latitude, lng: f.longitude, zoom: 17.5, key: `${id}:push` }); }
  }, [params, list]);

  // Refresh while something is building so it flips to active on time.
  useEffect(() => {
    if (!list.some((f) => f.status === 'building')) return;
    const id = setInterval(() => load(position), 10_000);
    return () => clearInterval(id);
  }, [list, position, load]);

  const checkedInId = device?.checked_in_field_id ?? null;

  // Foreground fallback for leaving a field: field alerts handle this with the
  // app closed, but with the map open we can see it ourselves. Disconnect once
  // the position has stayed outside the field (plus grace and GPS error) for
  // OUT_OF_FIELD_MS, so a single noisy fix cannot cut the connection.
  const outSince = useRef(null);
  useEffect(() => {
    const f = checkedInId ? list.find((x) => x.id === checkedInId) : null;
    if (!f || !position || !device) { outSince.current = null; return; }
    const d = distanceMeters(position, { lat: f.latitude, lng: f.longitude });
    const inside = d <= f.radius_m + CHECKIN_GRACE_M + Math.min(position.accuracy ?? 0, 50);
    if (inside) { outSince.current = null; return; }
    outSince.current ??= Date.now();
    if (Date.now() - outSince.current < OUT_OF_FIELD_MS) return;
    outSince.current = null;
    fieldsApi.checkOut(device.id)
      .then(() => { refresh(); say('success', 'You left the field — your device disconnected and keeps its charge.'); })
      .catch((e) => console.warn('auto check-out failed', e));
  }, [position?.lat, position?.lng, checkedInId, list, device?.id]);
  const selectedField = useMemo(
    () => list.find((f) => f.id === selected) ?? null, [list, selected]);

  const say = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 5000);
  };

  const create = async ({ intensity, radiusMeters, isPublic }) => {
    const field = await fieldsApi.create({
      lat: position.lat, lng: position.lng, radiusMeters, intensity, isPublic,
    });
    setCreating(false);
    setFocus({ lat: field.latitude ?? position.lat, lng: field.longitude ?? position.lng, zoom: 17.5, key: field.id });
    await Promise.all([load(position), refresh()]);
    say('success', `Field building — ready in ${Math.round((new Date(field.activates_at) - Date.now()) / 60000)} min.`);
  };

  const act = async (fn, okMessage) => {
    setBusy(true);
    try {
      await fn();
      await Promise.all([load(position), refresh()]);
      say('success', okMessage);
      setSelected(null);
    } catch (e) {
      const msg = e instanceof ApiError
        ? e.isTooFar ? 'You need to be closer to that field.'
        : e.isInsufficientTokens ? 'Not enough tokens.'
        : e.message
        : e.message;
      say('error', msg);
    } finally { setBusy(false); }
  };

  const nearEnough = selectedField && position
    ? distanceMeters(position, { lat: selectedField.latitude, lng: selectedField.longitude })
        <= selectedField.radius_m + CHECKIN_GRACE_M
    : false;

  // Phone: the whole screen above the tab bar, with its own floating chrome.
  // Desktop: a rounded map card that fills the main column's padded height.
  return (
    <div className="relative -mx-5 md:mx-0 h-[calc(100dvh-var(--tabbar-h)-env(safe-area-inset-bottom))] md:h-[calc(100dvh-4rem)] min-h-[420px]
                    md:rounded-[var(--radius-3xl)] md:overflow-hidden md:border"
         style={{ borderColor: 'var(--edge)' }}>
      <div className="absolute inset-0">
        <EnergyMap
          fields={list}
          center={position}
          userPosition={position}
          checkedInId={checkedInId}
          focus={focus}
          onSelectField={(id) => {
            setSelected(id);
            const f = list.find((x) => x.id === id);
            if (f) setFocus({ lat: f.latitude, lng: f.longitude, zoom: 17.5, key: `${id}:${Date.now()}` });
          }}
        />
      </div>

      {/* floating chrome: back + balance on the left; the right stays free for the zoom controls */}
      <div className="absolute left-3 right-3 flex items-center gap-2 pointer-events-none z-10"
           style={{ top: 'calc(env(safe-area-inset-top) + 0.75rem)' }}>
        <button onClick={() => nav(-1)} aria-label="Back" className="pointer-events-auto md:hidden glass-flat pill p-2"
                style={{ color: 'var(--text-2)' }}>
          <ChevronLeft size={16} />
        </button>
        <TokenPill tokens={tokens} className="pointer-events-auto !py-2" />
        {tokens < 20 && (
          <Button variant="gold" size="sm" className="pointer-events-auto"
                  onClick={() => nav('/subscription?tab=tokens')}>Get more</Button>
        )}
      </div>

      {/* location trouble */}
      {status !== 'ready' && (
        <div className="absolute left-3 right-3 z-10 glass !rounded-2xl px-4 py-3 text-sm flex items-center gap-3"
             style={{ top: 'calc(env(safe-area-inset-top) + 4rem)' }}>
          <span className="h-8 w-8 rounded-full grid place-items-center shrink-0"
                style={{ background: 'color-mix(in oklab, var(--gold) 16%, transparent)', color: 'var(--gold)' }}>
            {status === 'locating' ? <Loader2 size={15} className="animate-spin" /> : <AlertTriangle size={15} />}
          </span>
          <span className="flex-1" style={{ color: 'var(--text-2)' }}>
            {status === 'denied' ? 'Location access is off — enable it to create or join fields.'
             : status === 'locating' ? 'Finding your location…'
             : 'Location unavailable.'}
          </span>
          {status !== 'locating' && (
            <button onClick={retry} className="btn btn-glass !py-1.5 !px-3 text-xs">
              <RefreshCw size={12} /> Retry
            </button>
          )}
        </div>
      )}

      {/* toast */}
      {toast && (
        <div className="absolute bottom-24 left-3 right-3 z-10 glass !rounded-2xl px-4 py-3 text-sm flex items-start gap-3"
             style={{ borderColor: `color-mix(in oklab, ${toast.type === 'error' ? 'var(--rust)' : 'var(--sage)'} 55%, transparent)` }}>
          <span className="mt-1.5 h-2 w-2 rounded-full shrink-0"
                style={{ background: toast.type === 'error' ? 'var(--rust)' : 'var(--sage)',
                         boxShadow: `0 0 12px ${toast.type === 'error' ? 'var(--rust)' : 'var(--sage)'}` }} />
          <span className="flex-1" style={{ color: 'var(--text-2)' }}>{toast.message}</span>
          <button onClick={() => setToast(null)} className="opacity-60 hover:opacity-100"><X size={14} /></button>
        </div>
      )}

      {/* selected field sheet */}
      {selectedField && (
        <div className="absolute bottom-20 left-3 right-3 glass p-5 space-y-4 z-10">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="eyebrow">{selectedField.is_mine ? 'Your field' : selectedField.is_public ? 'Public field' : 'Field'}</p>
              <p className="display text-[1.6rem] capitalize mt-1">
                {selectedField.intensity} · {selectedField.radius_m}m
              </p>
              <p className="text-xs mt-1.5 flex items-center gap-2 flex-wrap" style={{ color: 'var(--text-3)' }}>
                <Tag tone={selectedField.status === 'building' ? 'building' : selectedField.status === 'expired' ? 'expired' : 'active'} dot>
                  {selectedField.status}
                </Tag>
                <span>
                  {selectedField.status === 'building' ? 'Forming…'
                    : selectedField.status === 'expired' ? 'Expired'
                    : `Until ${new Date(selectedField.expires_at).toLocaleString(undefined,
                        { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`}
                  {' · '}{Math.round(selectedField.distance_m)}m away
                </span>
              </p>
            </div>
            <button onClick={() => { setSelected(null); setConfirmRemove(false); }} className="btn btn-glass !p-2 !rounded-full"><X size={15} /></button>
          </div>

          <div className="flex gap-2 flex-wrap">
            {selectedField.status === 'active' && checkedInId !== selectedField.id && (
              <Button variant="mint" disabled={busy || !nearEnough}
                      onClick={() => act(
                        () => fieldsApi.checkIn({ deviceId: device.id, fieldId: selectedField.id,
                                                  lat: position.lat, lng: position.lng }),
                        'Connected — your device is charging.')}>
                <Zap size={14} />
                {nearEnough ? 'Connect' : 'Too far to connect'}
              </Button>
            )}
            {checkedInId === selectedField.id && (
              <Button variant="glass" disabled={busy}
                      onClick={() => act(() => fieldsApi.checkOut(device.id), 'Disconnected.')}>
                Disconnect
              </Button>
            )}
            {(selectedField.is_mine || selectedField.is_public) && selectedField.status !== 'building' && (
              <Button variant="glass" disabled={busy}
                      onClick={() => act(() => fieldsApi.reenergize(selectedField.id), 'Field re-energized.')}>
                <Sparkles size={14} />
                Re-energize · {reenergizeCost({ is_public: selectedField.is_public,
                                                token_cost: selectedField.token_cost })} tokens
              </Button>
            )}
            {/* Creators may remove their private fields; keepers may remove any.
                Two taps, so a mis-tap on a small card cannot cancel a field. */}
            {(isAdmin || (selectedField.is_mine && !selectedField.is_public)) && selectedField.status !== 'expired' && (
              confirmRemove ? (
                <Button variant="quiet" disabled={busy} style={{ color: 'var(--rust)' }}
                        onClick={() => { setConfirmRemove(false);
                          act(() => fieldsApi.remove(selectedField.id), 'Field removed. Tokens are not refunded.'); }}>
                  <Trash2 size={14} /> Really remove{isAdmin && !selectedField.is_mine ? ' (keeper)' : ''}?
                </Button>
              ) : (
                <Button variant="quiet" disabled={busy} onClick={() => setConfirmRemove(true)}>
                  <Trash2 size={14} /> Remove
                </Button>
              )
            )}
          </div>
        </div>
      )}

      {/* primary action */}
      <div className="absolute bottom-4 left-3 right-3 flex items-center gap-2 z-10">
        <Button variant="mint" size="lg" className="flex-1 !py-3.5" disabled={status !== 'ready'} onClick={() => setCreating(true)}>
          <Plus size={16} /> Energize this area
        </Button>
        <button onClick={retry} title="Recentre" className="btn btn-glass !p-3 !rounded-full">
          <LocateFixed size={18} />
        </button>
      </div>

      {creating && (
        <FieldModal
          tokens={tokens}
          fields={list}
          position={position}
          onCancel={() => setCreating(false)}
          onCreate={create}
          onBuyTokens={() => nav('/subscription?tab=tokens')}
        />
      )}
    </div>
  );
}
